import React from 'react';
import { InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormInputText } from '../../../../form-components/FormInputText';
import { FormInputDate } from '../../../../form-components/FormInputDate';
import { FormInputRadio } from '../../../../form-components/FormInputRadio';
import { FormInputDropdown } from '../../../../form-components/FormInputDropdown';
import { FormInputPhone } from '../../../../form-components/FormInputPhone';
import CommonModalLayout from '../../../../form-components/CommonModalLayout';
import CommonModalButton from '../../../../form-components/CommonModalButton';
import CommonText from '../../../../form-components/CommonText';
import { gender, smsAgreement } from '../../../../constants/constants';
import { VISIT_PATH_ETC_MAX_LENGTH, VISIT_PATH_OPTIONS, VISIT_PATH_OTHER } from '../../../../constants/visitPath';
import MySearchIcon from '../../../MySearchIcon';
import AppLinkStatus, { useBranchAppLink } from '../../../memberAppLink/AppLinkStatus';
import {
  FormWrapper,
  FormGrid,
  SectionDivider,
  FieldRow,
  FieldRowFull,
  FieldRowLabel,
  FieldRowControl,
  AgeText,
  AddressHelperText,
} from './UserDefaultModalStyled';

// 회원의 가입 경로(BE의 loginType 코드 → 화면 라벨) 매핑.
const LOGIN_TYPE_LABELS = {
  K: '카카오계정',
  A: '애플계정',
  N: '네이버계정',
  G: '구글계정',
  B: '일반로그인',
};

const formatLoginType = loginType => LOGIN_TYPE_LABELS[loginType] ?? '-';

// 회원 일반 정보 수정 모달 — 모달 wrapper + 폼 본체를 한 컴포넌트로 캡슐화.
// useForm 상태(control / watchedFields / setValue 등)는 부모(UserGeneralTab)가 보유 —
// 페이지 레벨 폼과 모달이 동일 폼을 공유하기 때문에 props로 전달받는다.
const UserDefaultModal = ({
  open,
  onClose,
  onSave,
  // form state (react-hook-form)
  control,
  setValue,
  watchedFields,
  password,
  // user data
  user,
  // address (daum-postcode)
  address,
  handleClick,
}) => {
  const { t } = useTranslation();

  const { currentLink } = useBranchAppLink({ type: 'MEMBER', id: user.memberId });
  const isAppLinked = !!currentLink;

  return (
    <CommonModalLayout
      open={open}
      onClose={onClose}
      title={t('General Information')}
      width={900}
      footer={
        <>
          <CommonModalButton variant="cancel" onClick={onClose}>
            {t('Cancel')}
          </CommonModalButton>
          <CommonModalButton variant="submit" onClick={onSave}>
            {t('Save')}
          </CommonModalButton>
        </>
      }>
      {/* 필수 항목 안내 — 빨간 강조라 description prop 대신 콘텐츠 상단에 직접 배치. */}
      <CommonText variant="hint" sx={{ color: 'red', display: 'block', mb: 2 }}>
        {t(
          'Terms marked with ( * ) must be entered. Using administrator privileges, you can change details like name, mobile number, email, etc. without checking for duplication',
        )}
      </CommonText>

      <FormWrapper>
        {/* 그리드는 row-major. JSX 순서가 [좌1, 우1, 좌2, 우2, ...]가 되도록 zigzag로 배치. */}
        <FormGrid className="form1">
          {/* row 1: 이름 / 전화번호 */}
          <FieldRow>
            <FieldRowLabel>이름</FieldRowLabel>
            <FieldRowControl>
              <FormInputText
                control={control}
                rules={{ required: true }}
                name="name"
                placeholder={t('Please enter your name')}
              />
            </FieldRowControl>
          </FieldRow>
          <FieldRow>
            <FieldRowLabel>{t('Phone Number*')}</FieldRowLabel>
            <FieldRowControl>
              <FormInputPhone
                control={control}
                setValue={setValue}
                rules={{ required: true }}
                name="phoneNumber"
                placeholder={t('Enter your phone number')}
              />
              {/* 번호는 가족 공용·더미일 수 있어 사람을 가리지 않는다 — 중복 확인 없이 저장한다(2026-10-28) */}
            </FieldRowControl>
          </FieldRow>
          {/* row 2: 이메일 / 생년월일 */}
          <FieldRow>
            <FieldRowLabel>{t('E-mail*')}</FieldRowLabel>
            <FieldRowControl>
              <FormInputText
                control={control}
                type="email"
                name="email"
                placeholder={t('Enter your E-mail address')}
              />
            </FieldRowControl>
          </FieldRow>
          <FieldRow>
            <FieldRowLabel>{t('Date of Birth')}</FieldRowLabel>
            <FieldRowControl>
              <FormInputDate name="birthDate" control={control} placeholder={t('날짜를 선택해주세요')} />
              <AgeText>{watchedFields.age ? `${watchedFields.age}세` : ''}</AgeText>
            </FieldRowControl>
          </FieldRow>
          {/* row 3: 방문경로 / 등록일 — 둘 다 이 지점 값이다(백로그 #170). 방문경로는 잠재회원 전환 시 승계되며 매출 통계에도 잡힌다. */}
          <FieldRow>
            <FieldRowLabel>{t('방문경로')}</FieldRowLabel>
            {/* 직접 입력란은 드롭다운 아래로 쌓는다 — 가로로 두면 입력란이 폭을 다 가져가 드롭다운이 찌그러진다 */}
            <FieldRowControl sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <FormInputDropdown
                name="visitPath"
                control={control}
                sx={{ width: '100%' }}
                options={[{ value: '', label: '선택 안 함' }, ...VISIT_PATH_OPTIONS]}
              />
              {watchedFields.visitPath === VISIT_PATH_OTHER && (
                <FormInputText
                  control={control}
                  name="visitPathEtc"
                  placeholder={`방문경로 직접 입력 (${VISIT_PATH_ETC_MAX_LENGTH}자 이내)`}
                  inputProps={{ maxLength: VISIT_PATH_ETC_MAX_LENGTH }}
                />
              )}
            </FieldRowControl>
          </FieldRow>
          <FieldRow>
            <FieldRowLabel>{t('Registration date​​')}</FieldRowLabel>
            <FieldRowControl>
              <FormInputDate
                name="branchRegisteredDate"
                control={control}
                placeholder={t('날짜를 선택해주세요')}
              />
            </FieldRowControl>
          </FieldRow>

          {/* row 4: 성별 / 광고성 SMS 수신 — 방문경로·등록일과 자리 바꿈(2026-10-22) */}
          <FieldRow>
            <FieldRowLabel>{t('Gender')}</FieldRowLabel>
            <FieldRowControl>
              <FormInputRadio name="gender" control={control} options={gender} row />
            </FieldRowControl>
          </FieldRow>
          <FieldRow>
            <FieldRowLabel>{t('Agree to receive SMS')}</FieldRowLabel>
            <FieldRowControl>
              <FormInputRadio name="isSMSReceive" control={control} options={smsAgreement} row />
            </FieldRowControl>
          </FieldRow>

          {/* row 5: 앱 연동(전체 폭) — 지점 단위. 지점명 칸이 아니라 로그인한 지점(오른쪽 위) 기준이다(2026-10-21).
              다지점 이용권으로 쓸 수 있는 지점이라도 연동한 건 아니다 */}
          {user.memberId && (
            <FieldRowFull>
              <FieldRowLabel>앱 연동</FieldRowLabel>
              <FieldRowControl>
                <AppLinkStatus
                  target={{
                    type: 'MEMBER',
                    id: user.memberId,
                    name: user.fullname || user.name,
                    phoneNumber: user.phoneNumber,
                    appLoginEnabled: user.appLoginEnabled === true,
                    appUsername: user.username,
                  }}
                />
              </FieldRowControl>
            </FieldRowFull>
          )}

          {/* row 6~7: ID / 로그인 타입, 비밀번호 / 비밀번호 확인. 앱 계정(아이디·비밀번호·로그인 타입)은 앱 로그인용이라 이 지점과 연동된 회원에게만 보인다(2026-10-22).
              연동 전 회원은 아이디가 없는 게 정상이고, 연동하면 아이디 확인과 비밀번호 재설정(복구용)을 여기서 한다 */}
          {isAppLinked && (
            <>
              <FieldRow>
                <FieldRowLabel>{t('ID*')}</FieldRowLabel>
                <FieldRowControl>
                  <FormInputText
                    control={control}
                    disabled
                    rules={{ required: true }}
                    staticValue={
                      user.loginType === 'B' && user.username !== 'temp' + user.phoneNumber ? user.username : ''
                    }
                    placeholder={t('Enter your ID')}
                  />
                </FieldRowControl>
              </FieldRow>

              <FieldRow>
                <FieldRowLabel>{t('Login Type')}</FieldRowLabel>
                <FieldRowControl>
                  <FormInputText control={control} disabled staticValue={formatLoginType(user.loginType)} />
                </FieldRowControl>
              </FieldRow>

              <FieldRow>
                <FieldRowLabel>{t('Password')}</FieldRowLabel>
                <FieldRowControl>
                  <FormInputText
                    control={control}
                    type="password"
                    name="password"
                    disabled={user.loginType !== 'B'}
                    rules={{
                      pattern: {
                        // 회원앱 비밀번호 규칙(서버 AppPasswordRule) — 4~16자, 조합 제한 없음(2026-10-28)
                        value: /^.{4,16}$/u,
                        message: '비밀번호는 4~16자로 입력해 주세요.',
                      },
                    }}
                    placeholder="4~16자"
                  />
                </FieldRowControl>
              </FieldRow>

              <FieldRow>
                <FieldRowLabel>{t('Confirm Password')}</FieldRowLabel>
                <FieldRowControl>
                  <FormInputText
                    control={control}
                    type="password"
                    name="verifyPassword"
                    disabled={user.loginType !== 'B'}
                    rules={{
                      validate: value => value === password || '비밀번호가 일치하지 않습니다',
                    }}
                    placeholder={t('비밀번호를 한 번 더 입력해주세요')}
                  />
                </FieldRowControl>
              </FieldRow>

            </>
          )}

          <SectionDivider />

          {/* 주소 — 두 컬럼을 가로지름 */}
          <FieldRowFull>
            <FieldRowLabel>{t('Select Address')}</FieldRowLabel>
            <FieldRowControl>
              <FormInputText
                control={control}
                name="address"
                disabled
                onMouseOver={e => (e.target.style.cursor = 'pointer')}
                placeholder={t('Address/ZIP code search')}
                sx={{
                  '& .css-nxo287-MuiInputBase-input-MuiOutlinedInput-input.Mui-disabled': {
                    '-webkit-text-fill-color': '#000 !important',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {!address && <AddressHelperText>{t('City, State or ZIP Code')}</AddressHelperText>}
                      <MySearchIcon onClick={handleClick} />
                    </InputAdornment>
                  ),
                }}
              />
            </FieldRowControl>
          </FieldRowFull>
          <FieldRowFull>
            <FieldRowLabel>상세 주소</FieldRowLabel>
            <FieldRowControl>
              <FormInputText control={control} name="addressDetail" placeholder={t('Address details')} />
            </FieldRowControl>
          </FieldRowFull>
        </FormGrid>
      </FormWrapper>
    </CommonModalLayout>
  );
};

export default UserDefaultModal;
